import { useState } from 'react';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors, DragOverlay, DragStartEvent , useDroppable , useDraggable } from '@dnd-kit/core';
import { useNavigate } from 'react-router-dom';
import { User, Phone } from 'lucide-react';
import { formatLeadPhone } from '@/components/ui/PhoneInput';

interface LeadStatus {
  id: string;
  name: string;
  color: string;
  order: number;
}

interface Lead {
  id: string;
  name: string;
  phones: any;
  emails: any;
  source: string | null;
  status: string;
  assignedTo?: { name: string } | null;
}

interface Props {
  leads: Lead[];
  statuses: LeadStatus[];
  onStatusChange: (leadId: string, newStatus: string) => void;
}

function KanbanCard({ lead, isDragging }: { lead: Lead; isDragging?: boolean }) {
  const navigate = useNavigate();
  // lead.phones is now LeadPhone[] (backend orders primary first).
  const phone = formatLeadPhone((lead.phones as any)?.[0]);

  return (
    <div
      onClick={() => !isDragging && navigate(`/leads/${lead.id}`)}
      className={`bg-white rounded-lg border border-gray-100 p-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${isDragging ? 'opacity-50' : ''}`}
    >
      <p className="text-sm font-semibold text-slate-800 truncate">{lead.name}</p>
      {phone && (
        <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
          <Phone className="h-3 w-3" /> {phone}
        </p>
      )}
      <div className="flex items-center justify-between mt-2">
        {lead.source && (
          <span className="text-[10px] font-medium bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{lead.source}</span>
        )}
        {lead.assignedTo && (
          <span className="text-[10px] text-slate-400 flex items-center gap-1">
            <User className="h-3 w-3" /> {lead.assignedTo.name}
          </span>
        )}
      </div>
    </div>
  );
}

function DraggableCard({ lead }: { lead: Lead }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: lead.id,
    data: { lead },
  });

  return (
    <div ref={setNodeRef} {...listeners} {...attributes}>
      <KanbanCard lead={lead} isDragging={isDragging} />
    </div>
  );
}

function DroppableColumn({ status, leads, color }: { status: string; leads: Lead[]; color: string }) {
  const { isOver, setNodeRef } = useDroppable({ id: status });

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-[280px] bg-slate-50 rounded-xl flex flex-col max-h-full ${isOver ? 'ring-2 ring-[#6366F1]' : ''}`}
    >
      {/* Column header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200">
        <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
        <span className="text-xs font-bold text-slate-700 uppercase tracking-wide truncate">{status}</span>
        <span className="text-[10px] font-bold bg-slate-200 text-slate-600 rounded-full px-1.5 py-0.5 ml-auto">{leads.length}</span>
      </div>

      {/* Cards */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {leads.length === 0 ? (
          <p className="text-xs text-slate-300 text-center py-6">No leads</p>
        ) : (
          leads.map(lead => <DraggableCard key={lead.id} lead={lead} />)
        )}
      </div>
    </div>
  );
}

export function KanbanBoard({ leads, statuses, onStatusChange }: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const sortedStatuses = [...statuses].sort((a, b) => a.order - b.order);
  const statusColors: Record<string, string> = {};
  sortedStatuses.forEach(s => { statusColors[s.name] = s.color; });

  const leadsByStatus: Record<string, Lead[]> = {};
  sortedStatuses.forEach(s => { leadsByStatus[s.name] = []; });
  leads.forEach(lead => {
    // Match lead status to a known status (case-insensitive)
    const matched = sortedStatuses.find(s => s.name.toLowerCase() === (lead.status || '').toLowerCase());
    const key = matched?.name || lead.status || 'New';
    if (!leadsByStatus[key]) leadsByStatus[key] = [];
    leadsByStatus[key].push(lead);
  });

  const activeLead = activeId ? leads.find(l => l.id === activeId) : null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const leadId = active.id as string;
    const newStatus = over.id as string;
    const lead = leads.find(l => l.id === leadId);
    if (lead && lead.status !== newStatus) {
      onStatusChange(leadId, newStatus);
    }
  };

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '60vh' }}>
        {sortedStatuses.map(s => (
          <DroppableColumn
            key={s.name}
            status={s.name}
            leads={leadsByStatus[s.name] || []}
            color={s.color}
          />
        ))}
      </div>

      <DragOverlay>
        {activeLead ? <KanbanCard lead={activeLead} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
